import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const converted = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      converted[camelKey] = typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])
        ? toCamelCase(obj[key])
        : obj[key];
    }
  }
  return converted;
};

export const repairGuideService = {
  /**
   * Fetches the active/published repair guides for a given product or component,
   * including all associated steps and actions.
   */
  async getByProductOrComponent(productId, componentId) {
    let query = supabase
      .from('repair_guides')
      .select(`
        *,
        author:users!repair_guides_user_id_fkey(id, email, first_name, last_name)
      `);

    if (productId) {
      query = query.eq('product_id', productId);
    } else if (componentId) {
      query = query.eq('component_id', componentId);
    } else {
      return [];
    }

    const { data: guides, error: guidesError } = await query.order('created_at', { ascending: false });
    if (guidesError) throw guidesError;

    const guidesWithDetails = [];

    for (const guide of guides) {
      // Fetch steps ordered by step_number
      const { data: steps, error: stepsError } = await supabase
        .from('repair_guide_steps')
        .select('*')
        .eq('guide_id', guide.id)
        .order('step_number', { ascending: true });

      if (stepsError) throw stepsError;

      const stepsWithActions = [];

      for (const step of steps) {
        // Fetch actions for each step
        const { data: actions, error: actionsError } = await supabase
          .from('repair_guide_step_actions')
          .select('*')
          .eq('step_id', step.id);

        if (actionsError) throw actionsError;

        stepsWithActions.push({
          ...toCamelCase(step),
          actions: actions?.map(toCamelCase) || []
        });
      }

      guidesWithDetails.push({
        ...toCamelCase(guide),
        steps: stepsWithActions
      });
    }

    return guidesWithDetails;
  },

  /**
   * Saves (inserts or updates) a complete repair guide with its steps and actions.
   * Transaction-like logic on the frontend:
   * 1. Upsert the repair_guide
   * 2. Delete existing steps for that guide (which cascades to actions)
   * 3. Insert new steps and their associated actions sequentially to preserve IDs and ordering
   */
  async saveFullGuide(guideData, steps, userId) {
    const activeUserId = userId || guideData.userId;
    if (!activeUserId) throw new Error('Not authenticated');

    // 1. Save main guide row
    const guidePayload = {
      product_id: guideData.productId || null,
      component_id: guideData.componentId || null,
      title: guideData.title,
      description: guideData.description || '',
      is_published: guideData.isPublished !== undefined ? guideData.isPublished : false,
      estimated_time: guideData.estimatedTime || null,
      user_id: activeUserId
    };

    let guideId = guideData.id;
    if (guideId) {
      const { error: updateError } = await supabase
        .from('repair_guides')
        .update(guidePayload)
        .eq('id', guideId);
      if (updateError) throw updateError;
    } else {
      const { data: newGuide, error: insertError } = await supabase
        .from('repair_guides')
        .insert(guidePayload)
        .select()
        .single();
      if (insertError) throw insertError;
      guideId = newGuide.id;
    }

    // 2. Delete old steps if editing
    if (guideData.id) {
      const { error: deleteError } = await supabase
        .from('repair_guide_steps')
        .delete()
        .eq('guide_id', guideId);
      if (deleteError) throw deleteError;
    }

    // 3. Insert new steps & actions
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const stepPayload = {
        guide_id: guideId,
        step_number: i + 1,
        title: step.title || `Step ${i + 1}`,
        notes: step.notes || '',
        camera_angles: step.cameraAngles || []
      };

      const { data: newStep, error: stepError } = await supabase
        .from('repair_guide_steps')
        .insert(stepPayload)
        .select()
        .single();

      if (stepError) throw stepError;

      if (step.actions && step.actions.length > 0) {
        const actionsPayload = step.actions.map(action => ({
          step_id: newStep.id,
          part_name: action.partName,
          target_x: action.targetX || 0,
          target_y: action.targetY || 0,
          target_z: action.targetZ || 0,
          target_rx: action.targetRx || 0,
          target_ry: action.targetRy || 0,
          target_rz: action.targetRz || 0,
          target_px: action.targetPx || 0,
          target_py: action.targetPy || 0,
          target_pz: action.targetPz || 0,
          is_removed: action.isRemoved !== undefined ? action.isRemoved : false
        }));

        const { error: actionsError } = await supabase
          .from('repair_guide_step_actions')
          .insert(actionsPayload);

        if (actionsError) throw actionsError;
      }
    }

    return guideId;
  },

  /**
   * Deletes a guide and all its nested cascading child rows (steps, actions).
   */
  async deleteGuide(guideId) {
    const { error } = await supabase
      .from('repair_guides')
      .delete()
      .eq('id', guideId);

    if (error) throw error;
    return true;
  }
};
